import { Text } from '@blueprintjs/core'
import { GradientBorder } from 'client/components/GradientBorder/GradientBorder'
import { ResponsiveBlock } from 'client/components/ResponsiveBlock/ResponsiveBlock'
import { ClientLocaleNS } from 'lib/constants/locales'
import { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from 'client/components/Button/Button'
import { useFormik } from 'formik'
import { SIGNIN_FORM_SCHEMA } from 'lib/constants/validation'
import cn from 'classnames'
import {
  FormField,
  TFormFieldProps,
} from 'client/contents/CheckoutPageContent/components/OrderForm/components/FormField/FormField'
import { Link, router, usePage } from '@inertiajs/react'

type TProps = {}

type TPageProps = {
  alert?: string
}

type TSignInForm = {
  email_address: string
  password: string
}
type TSignInFormFieldKey = keyof TSignInForm
type TSubmitResult = {
  text: string
  status: 'success' | 'failure'
} | null

const INITIAL_VALUES: TSignInForm = {
  email_address: '',
  password: '',
}

const SignInForm: FC<TProps> = () => {
  const {
    props: { alert },
  } = usePage<TPageProps>()
  const { t } = useTranslation(ClientLocaleNS.ProfilePage)
  const [submitResult, setSubmitResult] = useState<TSubmitResult>(null)
  useEffect(() => {
    if (alert) {
      setSubmitResult({ text: alert, status: 'failure' })
    }
  }, [])
  const { handleChange, handleSubmit, errors, values, setErrors } = useFormik<TSignInForm>({
    initialValues: INITIAL_VALUES,
    validationSchema: SIGNIN_FORM_SCHEMA,
    validateOnChange: false,
    validateOnBlur: true,
    onSubmit: (values, { validateForm }) => {
      validateForm(values).then(() => {
        router.visit('/sign_in', {
          method: 'post',
          data: {
            ...values,
          },
        })
      })
    },
  })

  const getFormFieldProps = (key: TSignInFormFieldKey): TFormFieldProps => ({
    label: t(`sign_in.form.fields.${key}.title`),
    name: key,
    id: key,
    type: 'text',
    value: values[key],
    onChange: handleChange,
    error: errors[key],
  })

  return (
    <ResponsiveBlock className="py-30" contentClassName="justify-center">
      <GradientBorder>
        <form className="flex w-full flex-col p-12" onSubmit={handleSubmit}>
          <div className="flex flex-col gap-12">
            <div className="flex flex-col w-full gap-2">
              <Text tagName="span" className="font-body---20px">
                {t('sign_in.title')}
              </Text>
            </div>
            <FormField {...getFormFieldProps('email_address')} />
            <FormField {...getFormFieldProps('password')} type="password" />
          </div>
          <div className="flex w-full flex-1 flex-col items-center lg:items-start">
            <div className="my-4 flex w-full flex-1 items-center justify-center lg:justify-start">
              <Text
                tagName="span"
                className={cn(
                  'font-primary-regular---15pt flex transition-opacity',
                  !!submitResult ? 'opacity-100' : 'opacity-0',
                  {
                    'text-green-500': submitResult?.status === 'success',
                    'text-palette-red': submitResult?.status === 'failure',
                  },
                )}
              >
                {submitResult?.text ?? ''}
              </Text>
            </div>
            <div className="flex flex-col w-full gap-4">
              <Button type="submit" className="h-12">
                <Text tagName="span">{t('sign_in.form.button')}</Text>
              </Button>
              <div className="inline-flex justify-center items-center text-center gap-2">
                <Text tagName="span" className="font-body---16px text-palette-grey--50">
                  {t('no_account')}
                </Text>
                <Link href="/sign_up">
                  <Text tagName="span" className="font-body---16px">
                    {t('sign_up')}
                  </Text>
                </Link>
              </div>
            </div>
          </div>
        </form>
      </GradientBorder>
    </ResponsiveBlock>
  )
}

export { SignInForm }
